/**
 * Client-side cost calculation engine.
 *
 * Reimplements the server-side Snowflake SQL CTE pipeline using pre-exported
 * JSON data files served from /cost_data/.
 *
 * Mirrors: packages/service-cost/src/handler/api/project/.../handler.ts
 */

import type { ScenarioConfiguration } from "src/service/cost/data/building";
import type { ProjectCostingCalculationHttpResource } from "src/service/cost/resource/project/project-costing-calculation-resource";
import type {
  ValueBreakdown,
  ValueBreakdownArea,
  ValueGroup,
} from "src/service/cost/data/value-breakdown";
import {
  aggregateValueBreakdown,
  aggregateValueGroups,
  recreateWithTotalForValueGroupAggregated,
} from "src/service/cost/utility/value-breakdown/aggregate-value-breakdown";
import { sortValueGroupIndexFunction } from "src/service/cost/data/value-index";

// ── JSON data types ──────────────────────────────────────────────────────────

type KitModule = { id: string; label: string };

type KitUnit = { id: string; label: string };

type KitUnitModule = {
  id: string;
  kit_unit_id: string;
  kit_module_id: string;
  quantity: number;
};

type KitModuleElementReference = {
  id: string;
  source_id: string;
  index_group: string;
  index_group_item: string;
  rate_cost: number;
  rate_cost_tall_building: number;
  rate_carbon: number;
  unit_of_measurement: string;
  cost_plan_section: string;
  percentage_targets: string | null; // JSON-encoded string e.g. "[\"recXXX\"]"
};

type KitModuleBillOfMaterial = {
  id: string;
  kit_module_id: string;
  kit_module_element_reference_id: string;
  quantity: number;
};

type KitBaseBuildElementReference = {
  id: string;
  source_id: string;
  index_group: string;
  index_group_item: string;
  rate_cost: number;
  rate_carbon: number;
  unit_of_measurement: string;
  area_adjustment: number | null;
  building_data_target: string | null;
  building_height_breakpoints: string | null; // e.g. "{seven_and_below,between_eight_and_twelve}"
  percentage_targets: string | null;
};

type CostData = {
  kitModule: KitModule[];
  kitUnit: KitUnit[];
  kitUnitModule: KitUnitModule[];
  kitModuleElementReference: KitModuleElementReference[];
  kitModuleBillOfMaterial: KitModuleBillOfMaterial[];
  kitBaseBuildElementReference: KitBaseBuildElementReference[];
};

// ── Lazy data loading (cached in worker scope) ───────────────────────────────

let costDataPromise: Promise<CostData> | null = null;

async function loadCostData(): Promise<CostData> {
  if (costDataPromise) return costDataPromise;
  costDataPromise = (async () => {
    const [
      kitModule,
      kitUnit,
      kitUnitModule,
      kitModuleElementReference,
      kitModuleBillOfMaterial,
      kitBaseBuildElementReference,
    ] = await Promise.all([
      fetch("/cost_data/kit_module.json").then((r) => r.json()),
      fetch("/cost_data/kit_unit.json").then((r) => r.json()),
      fetch("/cost_data/kit_unit_module.json").then((r) => r.json()),
      fetch("/cost_data/kit_module_element_reference.json").then((r) =>
        r.json()
      ),
      fetch("/cost_data/kit_module_bill_of_material.json").then((r) =>
        r.json()
      ),
      fetch("/cost_data/kit_base_build_element_reference.json").then((r) =>
        r.json()
      ),
    ]);
    return {
      kitModule,
      kitUnit,
      kitUnitModule,
      kitModuleElementReference,
      kitModuleBillOfMaterial,
      kitBaseBuildElementReference,
    };
  })();
  return costDataPromise;
}

// Drops the cached cost-data promise so the next `calculateCost` call
// refetches from /public/cost_data. Used by dev tooling that lets a
// stakeholder edit the JSON files and see the impact without reloading.
export function clearCostDataCache(): void {
  costDataPromise = null;
}

// ── Pure helpers ─────────────────────────────────────────────────────────────

/** Round to 2 decimal places (matches server toPrecision(x, 2)). */
function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

/** Get the NIA height breakpoint string for a given number of levels. */
function getHeightBreakpoint(
  levels: number
): "seven_and_below" | "between_eight_and_twelve" | "thirteen_and_above" {
  if (levels <= 7) return "seven_and_below";
  if (levels <= 12) return "between_eight_and_twelve";
  return "thirteen_and_above";
}

/** Is the building tall (> 7 levels)? Determines which rate_cost column to use. */
function isTall(levels: number): boolean {
  return levels > 7;
}

/** Parse PostgreSQL array literal "{a,b,c}" → ["a","b","c"]. */
function parseHeightBreakpoints(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .replace(/^\{/, "")
    .replace(/\}$/, "")
    .split(",")
    .filter(Boolean);
}

// Modular plan sections that count as "module cost" (not fit-out):
const OFFSITE = "01. Off Site";
const ONSITE = "02. On Site";

// ── Internal record type ─────────────────────────────────────────────────────

type Rec = { index: string; cost: number; carbon: number };

// ── ValueGroup creation ──────────────────────────────────────────────────────

/**
 * Convert flat records into ValueGroup<number>[] grouped by the index prefix.
 * Mirrors createValueGroupFromQueryRecords from the server.
 */
function makeValueGroups(records: Rec[], key: "cost" | "carbon"): ValueGroup<number>[] {
  const groups: { index: string; total: number; items: { index: string; value: number }[] }[] = [];

  for (const rec of records) {
    const dot = rec.index.indexOf(".");
    const groupIdx = dot >= 0 ? rec.index.substring(0, dot) : rec.index;

    let group = groups.find((g) => g.index === groupIdx);
    if (!group) {
      group = { index: groupIdx, total: 0, items: [] };
      groups.push(group);
    }

    const value = round2(rec[key]);
    if (value !== 0) {
      group.items.push({ index: rec.index, value });
    }
  }

  return groups
    .map((g) => ({
      index: g.index,
      total: round2(g.items.reduce((s, i) => s + i.value, 0)),
      items: [...g.items].sort(sortValueGroupIndexFunction),
    }))
    .filter((g) => g.total !== 0)
    .sort(sortValueGroupIndexFunction);
}

/**
 * Add on-cost groups (prelims, contingency, OHP) to a construction summary.
 * Mirrors withOnCostsForValueGroupConstruction from the server.
 */
function withOnCosts(
  summary: ValueGroup<number>[],
  prelims: number,
  contingencies: number,
  ohp: number
): ValueGroup<number>[] {
  const constructionTotal = summary.find((g) => g.index === "1")?.total;
  if (constructionTotal === undefined) return summary;

  const prelimsValue = round2(constructionTotal * prelims);
  const contingenciesValue = round2((constructionTotal + prelimsValue) * contingencies);
  const ohpValue = round2(
    (constructionTotal + prelimsValue + contingenciesValue) * ohp
  );

  summary.push(
    { index: "2", total: prelimsValue, items: [{ index: "2.1", value: prelimsValue }] },
    { index: "3", total: contingenciesValue, items: [{ index: "3.1", value: contingenciesValue }] },
    { index: "4", total: ohpValue, items: [{ index: "4.1", value: ohpValue }] }
  );

  return summary
    .map((g) => ({
      ...g,
      total: round2(g.items.reduce((s, i) => s + i.value, 0)),
      items: [...g.items].sort(sortValueGroupIndexFunction),
    }))
    .sort(sortValueGroupIndexFunction);
}

// ── Modular calculation ──────────────────────────────────────────────────────

type ModuleLineItem = {
  kit_module_id: string;
  kit_module_element_reference_id: string;
  index_group_item: string;
  cost: number;
  carbon: number;
  cost_plan_section: string;
};

/**
 * Compute modular summary (ms) and element reference (mer) records for one building.
 * Mirrors the ms_* and mer_* CTE stages in the server SQL.
 */
function computeModular(
  unitMix: ScenarioConfiguration["buildings"][number]["units"],
  levels: number,
  costData: CostData
): { ms: Rec[]; mer: Rec[] } {
  const {
    kitModule,
    kitUnit,
    kitUnitModule,
    kitModuleElementReference,
    kitModuleBillOfMaterial,
  } = costData;

  const kmerById = new Map(kitModuleElementReference.map((k) => [k.id, k]));
  const kmerBySourceId = new Map(kitModuleElementReference.map((k) => [k.source_id, k]));
  const kitUnitById = new Map(kitUnit.map((k) => [k.id, k]));
  const tall = isTall(levels);

  // ── Step 1: non-percentage BOM line items ──────────────────────────────────
  const nonPct: ModuleLineItem[] = [];
  for (const bom of kitModuleBillOfMaterial) {
    const kmer = kmerById.get(bom.kit_module_element_reference_id);
    if (!kmer || kmer.unit_of_measurement === "percentage") continue;
    const rate = tall ? kmer.rate_cost_tall_building : kmer.rate_cost;
    nonPct.push({
      kit_module_id: bom.kit_module_id,
      kit_module_element_reference_id: bom.kit_module_element_reference_id,
      index_group_item: kmer.index_group_item,
      cost: rate * bom.quantity,
      carbon: kmer.rate_carbon * bom.quantity,
      cost_plan_section: kmer.cost_plan_section,
    });
  }

  // ── Step 2: percentage BOM line items ──────────────────────────────────────
  const pct: ModuleLineItem[] = [];
  for (const kmer of kitModuleElementReference) {
    if (kmer.unit_of_measurement !== "percentage") continue;
    if (!kmer.percentage_targets) continue;

    const targetSourceIds: string[] = JSON.parse(kmer.percentage_targets);
    const targetIds = targetSourceIds
      .map((sid) => kmerBySourceId.get(sid)?.id)
      .filter((id): id is string => !!id);

    const rate = tall ? kmer.rate_cost_tall_building : kmer.rate_cost;

    // Aggregate non-pct costs per module for the target element references
    const byModule = new Map<string, { cost: number; carbon: number }>();
    for (const item of nonPct) {
      if (!targetIds.includes(item.kit_module_element_reference_id)) continue;
      const cur = byModule.get(item.kit_module_id) ?? { cost: 0, carbon: 0 };
      byModule.set(item.kit_module_id, {
        cost: cur.cost + item.cost,
        carbon: cur.carbon + item.carbon,
      });
    }

    for (const [moduleId, sums] of byModule) {
      pct.push({
        kit_module_id: moduleId,
        kit_module_element_reference_id: kmer.id,
        index_group_item: kmer.index_group_item,
        cost: sums.cost * rate,
        carbon: sums.carbon * kmer.rate_carbon,
        cost_plan_section: kmer.cost_plan_section,
      });
    }
  }

  const allItems = [...nonPct, ...pct];

  // ── Step 3: module base costs (kit_module_cost = OffSite + OnSite; fitout = total - module) ──
  const moduleCosts = new Map<string, { cost: number; carbon: number; fitout: number }>();
  for (const km of kitModule) {
    const items = allItems.filter((i) => i.kit_module_id === km.id);
    if (!items.length) continue;
    const totalBOM = items.reduce((s, i) => s + i.cost, 0);
    const moduleCost = items
      .filter((i) => i.cost_plan_section === OFFSITE || i.cost_plan_section === ONSITE)
      .reduce((s, i) => s + i.cost, 0);
    moduleCosts.set(km.id, {
      cost: moduleCost,
      carbon: items.reduce((s, i) => s + i.carbon, 0),
      fitout: totalBOM - moduleCost,
    });
  }

  // ── Step 4: unit costs (scale by kit_unit_module.quantity) ─────────────────
  const unitCosts = new Map<string, { cost: number; carbon: number; fitout: number }>();
  for (const kum of kitUnitModule) {
    const mc = moduleCosts.get(kum.kit_module_id);
    if (!mc) continue;
    const ku = kitUnitById.get(kum.kit_unit_id);
    if (!ku) continue;
    const cur = unitCosts.get(ku.label) ?? { cost: 0, carbon: 0, fitout: 0 };
    unitCosts.set(ku.label, {
      cost: cur.cost + kum.quantity * mc.cost,
      carbon: cur.carbon + kum.quantity * mc.carbon,
      fitout: cur.fitout + kum.quantity * mc.fitout,
    });
  }

  // ── Step 5: scale by unit mix ──────────────────────────────────────────────
  let totalModule = 0,
    totalCarbon = 0,
    totalFitout = 0;
  for (const mixItem of unitMix) {
    const uc = unitCosts.get(mixItem.unit);
    if (!uc) continue;
    totalModule += mixItem.quantity * uc.cost;
    totalCarbon += mixItem.quantity * uc.carbon;
    totalFitout += mixItem.quantity * uc.fitout;
  }

  const ms: Rec[] = [
    { index: "1.1", cost: round2(totalModule), carbon: round2(totalCarbon) },
    { index: "1.2", cost: round2(totalFitout), carbon: 0 },
  ];

  // ── Modular element reference (mer) ────────────────────────────────────────
  // Group allItems by (module_id, index_group_item) → then scale by unit-module → unit-mix

  // module × index_group_item → {cost, carbon}
  const moduleElem = new Map<string, { cost: number; carbon: number }>();
  for (const item of allItems) {
    const key = `${item.kit_module_id}:${item.index_group_item}`;
    const cur = moduleElem.get(key) ?? { cost: 0, carbon: 0 };
    moduleElem.set(key, {
      cost: cur.cost + item.cost,
      carbon: cur.carbon + item.carbon,
    });
  }

  // unit_label × index_group_item → {cost, carbon}
  const unitElem = new Map<string, { cost: number; carbon: number }>();
  for (const kum of kitUnitModule) {
    const ku = kitUnitById.get(kum.kit_unit_id);
    if (!ku) continue;
    for (const [key, costs] of moduleElem) {
      const sep = key.indexOf(":");
      if (key.substring(0, sep) !== kum.kit_module_id) continue;
      const idx = key.substring(sep + 1);
      const ukey = `${ku.label}:${idx}`;
      const cur = unitElem.get(ukey) ?? { cost: 0, carbon: 0 };
      unitElem.set(ukey, {
        cost: cur.cost + kum.quantity * costs.cost,
        carbon: cur.carbon + kum.quantity * costs.carbon,
      });
    }
  }

  // index_group_item → {cost, carbon} (scaled by unit mix)
  const elemCosts = new Map<string, { cost: number; carbon: number }>();
  for (const mixItem of unitMix) {
    for (const [ukey, uc] of unitElem) {
      const sep = ukey.lastIndexOf(":");
      if (ukey.substring(0, sep) !== mixItem.unit) continue;
      const idx = ukey.substring(sep + 1);
      const cur = elemCosts.get(idx) ?? { cost: 0, carbon: 0 };
      elemCosts.set(idx, {
        cost: cur.cost + mixItem.quantity * uc.cost,
        carbon: cur.carbon + mixItem.quantity * uc.carbon,
      });
    }
  }

  const mer: Rec[] = Array.from(elemCosts.entries()).map(([idx, c]) => ({
    index: idx,
    cost: round2(c.cost),
    carbon: round2(c.carbon),
  }));

  return { ms, mer };
}

// ── Base build calculation ───────────────────────────────────────────────────

/**
 * Compute base build summary (bbs) and element reference (bber) records.
 * Mirrors the bbs_* and bber_* CTE stages in the server SQL.
 */
function computeBaseBuild(
  levels: number,
  buildingData: Record<string, number>,
  costData: CostData
): { bbs: Rec[]; bber: Rec[] } {
  const { kitBaseBuildElementReference } = costData;

  const heightBreakpoint = getHeightBreakpoint(levels);
  const kbberBySourceId = new Map(kitBaseBuildElementReference.map((k) => [k.source_id, k]));

  // ── Step 1: non-percentage items ──────────────────────────────────────────
  const nonPct: { id: string; index_group_item: string; cost: number; carbon: number }[] = [];
  for (const kbber of kitBaseBuildElementReference) {
    if (kbber.unit_of_measurement === "percentage") continue;
    const bps = parseHeightBreakpoints(kbber.building_height_breakpoints);
    if (!bps.includes(heightBreakpoint)) continue;

    const dataValue = kbber.building_data_target
      ? (buildingData[kbber.building_data_target] ?? 0)
      : 0;

    let cost: number, carbon: number;
    if (kbber.unit_of_measurement === "once") {
      cost = kbber.rate_cost;
      carbon = kbber.rate_carbon;
    } else if (kbber.unit_of_measurement === "each") {
      cost = kbber.rate_cost * dataValue;
      carbon = kbber.rate_carbon * dataValue;
    } else {
      const adj = kbber.area_adjustment ?? 1;
      cost = kbber.rate_cost * dataValue * adj;
      carbon = kbber.rate_carbon * dataValue * adj;
    }

    nonPct.push({ id: kbber.id, index_group_item: kbber.index_group_item, cost, carbon });
  }

  // ── Step 2: percentage items ───────────────────────────────────────────────
  const pct: { index_group_item: string; cost: number; carbon: number }[] = [];
  for (const kbber of kitBaseBuildElementReference) {
    if (kbber.unit_of_measurement !== "percentage") continue;
    if (!kbber.percentage_targets) continue;

    const targetSourceIds: string[] = JSON.parse(kbber.percentage_targets);
    const targetIds = targetSourceIds
      .map((sid) => kbberBySourceId.get(sid)?.id)
      .filter((id): id is string => !!id);

    const sumCost = nonPct
      .filter((i) => targetIds.includes(i.id))
      .reduce((s, i) => s + i.cost, 0);
    const sumCarbon = nonPct
      .filter((i) => targetIds.includes(i.id))
      .reduce((s, i) => s + i.carbon, 0);

    pct.push({
      index_group_item: kbber.index_group_item,
      cost: sumCost * kbber.rate_cost,
      carbon: sumCarbon * kbber.rate_carbon,
    });
  }

  const allItems = [...nonPct, ...pct];

  // Summary: single total row
  const totalCost = allItems.reduce((s, i) => s + i.cost, 0);
  const totalCarbon = allItems.reduce((s, i) => s + i.carbon, 0);
  const bbs: Rec[] = [
    { index: "1.3", cost: round2(totalCost), carbon: round2(totalCarbon) },
  ];

  // Element reference: group by index_group_item
  const grouped = new Map<string, { cost: number; carbon: number }>();
  for (const item of allItems) {
    const cur = grouped.get(item.index_group_item) ?? { cost: 0, carbon: 0 };
    grouped.set(item.index_group_item, {
      cost: cur.cost + item.cost,
      carbon: cur.carbon + item.carbon,
    });
  }
  const bber: Rec[] = Array.from(grouped.entries()).map(([idx, c]) => ({
    index: idx,
    cost: round2(c.cost),
    carbon: round2(c.carbon),
  }));

  return { bbs, bber };
}

// ── Carbon calculation ───────────────────────────────────────────────────────

/** Look up the carbon value for an element index from a ValueGroup array. */
function carbonAt(groups: ValueGroup<number>[], fullIndex: string): number {
  const dot = fullIndex.indexOf(".");
  const groupIdx = dot >= 0 ? fullIndex.substring(0, dot) : fullIndex;
  const group = groups.find((g) => g.index === groupIdx);
  return group?.items.find((i) => i.index === fullIndex)?.value ?? 0;
}

/**
 * Compute embodied carbon groups from a cost breakdown.
 * Mirrors createEmbodiedCarbonCostElementReference from the server.
 */
function computeEmbodiedCarbon(breakdown: ValueBreakdown<number>): ValueGroup<number>[] {
  const mel = breakdown.modular.element;
  const bel = breakdown.basebuild.element;

  const sum = (...indexes: [src: ValueGroup<number>[], idx: string][]) =>
    round2(indexes.reduce((s, [src, idx]) => s + carbonAt(src, idx), 0));

  const result: ValueGroup<number>[] = [];

  // 1 – Substructure (note: server code sums 1.1 twice from both sources – preserved faithfully)
  const sub = sum(
    [mel, "1.1"], [bel, "1.1"],
    [mel, "1.1"], [bel, "1.1"]
  );
  if (sub !== 0) result.push({ index: "1", total: sub, items: [{ index: "1.1", value: sub }] });

  // 2 – Superstructure
  const superItems = [
    { index: "2.1", value: sum([mel, "6.2"], [bel, "6.2"], [mel, "2.1"], [bel, "2.1"]) },
    { index: "2.2", value: sum([mel, "2.2"], [bel, "2.2"]) },
    { index: "2.3", value: sum([mel, "2.3"], [bel, "2.3"]) },
    { index: "2.4", value: sum([mel, "2.4"], [bel, "2.4"]) },
    { index: "2.5", value: sum([mel, "2.5"], [bel, "2.5"]) },
    { index: "2.6", value: sum([mel, "2.6"], [bel, "2.6"]) },
    { index: "2.7", value: sum([mel, "2.7"], [bel, "2.7"]) },
    { index: "2.8", value: sum([mel, "2.8"], [bel, "2.8"]) },
  ].filter((i) => i.value !== 0);
  const superTotal = round2(superItems.reduce((s, i) => s + i.value, 0));
  if (superTotal !== 0) result.push({ index: "2", total: superTotal, items: superItems });

  // 3 – Internal Finishes
  const finishItems = [
    { index: "3.1", value: sum([mel, "3.1"], [bel, "3.1"]) },
    { index: "3.2", value: sum([mel, "3.2"], [bel, "3.2"]) },
    { index: "3.3", value: sum([mel, "3.3"], [bel, "3.3"]) },
  ].filter((i) => i.value !== 0);
  const finishTotal = round2(finishItems.reduce((s, i) => s + i.value, 0));
  if (finishTotal !== 0) result.push({ index: "3", total: finishTotal, items: finishItems });

  // 4 – Fittings
  const fit = sum([mel, "4.1"], [bel, "4.1"]);
  if (fit !== 0) result.push({ index: "4", total: fit, items: [{ index: "4.1", value: fit }] });

  // 5 – Services
  const servItems = [
    { index: "5.1", value: sum([mel, "5.1"], [bel, "5.1"]) },
    { index: "5.3", value: sum([mel, "5.3"], [bel, "5.3"]) },
    { index: "5.4", value: sum([mel, "5.4"], [bel, "5.4"]) },
    { index: "5.6", value: sum([mel, "5.6"], [bel, "5.6"]) },
    { index: "5.7", value: sum([mel, "5.7"], [bel, "5.7"]) },
    { index: "5.8", value: sum([mel, "5.8"], [bel, "5.8"]) },
    { index: "5.10", value: sum([mel, "5.10"], [bel, "5.10"]) },
  ].filter((i) => i.value !== 0);
  const servTotal = round2(servItems.reduce((s, i) => s + i.value, 0));
  if (servTotal !== 0) result.push({ index: "5", total: servTotal, items: servItems });

  return result;
}

// ── Public API ───────────────────────────────────────────────────────────────

export type CostEngineSettings = {
  /** Preliminaries percentage (e.g. 10 means 10%) */
  prelims: number;
  /** Contingency percentage (e.g. 3 means 3%) */
  contingency: number;
  /** Overheads and profit percentage (e.g. 5.5 means 5.5%) */
  ohp: number;
};

/**
 * Run the full cost calculation for a scenario.
 *
 * This is the client-side equivalent of the server's
 * PostProjectCostingCalculationResourceApiHandler.
 */
export async function calculateCost(
  config: ScenarioConfiguration,
  settings: CostEngineSettings
): Promise<ProjectCostingCalculationHttpResource> {
  const data = await loadCostData();

  const prelims = settings.prelims / 100;
  const contingencies = settings.contingency / 100;
  const ohp = settings.ohp / 100;

  // ── Per-building calculation ───────────────────────────────────────────────
  const buildingResults: {
    costing: ValueBreakdown<number>;
    carbon: ValueGroup<number>[];
  }[] = [];

  for (const building of config.buildings) {
    const levels = (building.data as Record<string, number>)["number_of_levels"] ?? 0;
    const buildingData = building.data as unknown as Record<string, number>;

    const { ms, mer } = computeModular(building.units, levels, data);
    const { bbs, bber } = computeBaseBuild(levels, buildingData, data);

    const costing: ValueBreakdown<number> = {
      modular: {
        summary: withOnCosts(makeValueGroups(ms, "cost"), prelims, contingencies, ohp),
        element: makeValueGroups(mer, "cost"),
      },
      basebuild: {
        summary: withOnCosts(makeValueGroups(bbs, "cost"), prelims, contingencies, ohp),
        element: makeValueGroups(bber, "cost"),
      },
    };

    const carbonBreakdown: ValueBreakdown<number> = {
      modular: {
        summary: makeValueGroups(ms, "carbon"),
        element: makeValueGroups(mer, "carbon"),
      },
      basebuild: {
        summary: makeValueGroups(bbs, "carbon"),
        element: makeValueGroups(bber, "carbon"),
      },
    };

    buildingResults.push({ costing, carbon: computeEmbodiedCarbon(carbonBreakdown) });
  }

  // ── Scenario (site-level) base build calculation ───────────────────────────
  // The server runs base build with scenario data + empty building data.
  // NumberOfLevels = 0 → height breakpoint = seven_and_below.
  const scenarioData: Record<string, number> = {
    ...(config.scenario as unknown as Record<string, number>),
  };
  const { bbs: scenBbs, bber: scenBber } = computeBaseBuild(0, scenarioData, data);

  const scenarioCosting: ValueBreakdownArea<number> = {
    summary: withOnCosts(makeValueGroups(scenBbs, "cost"), prelims, contingencies, ohp),
    element: makeValueGroups(scenBber, "cost"),
  };

  const scenarioCarbonBreakdown: ValueBreakdown<number> = {
    modular: { summary: [], element: [] },
    basebuild: {
      summary: makeValueGroups(scenBbs, "carbon"),
      element: makeValueGroups(scenBber, "carbon"),
    },
  };

  // ── Aggregate buildings ────────────────────────────────────────────────────
  const buildingsCosting = aggregateValueBreakdown(buildingResults.map((r) => r.costing));
  const buildingsCarbon = recreateWithTotalForValueGroupAggregated(
    aggregateValueGroups(buildingResults.map((r) => r.carbon))
  );

  return {
    configuration: {
      on_costs: {
        preliminiaries_percentage: settings.prelims,
        contingencies_percentage: settings.contingency,
        overheads_and_profits_percentage: settings.ohp,
      },
    },
    scenario: {
      costing: scenarioCosting,
      carbon: computeEmbodiedCarbon(scenarioCarbonBreakdown),
    },
    buildings: {
      costing: buildingsCosting,
      carbon: buildingsCarbon,
    },
  };
}
